import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Grid, TextField, RadioGroup, FormControlLabel, Radio, Stepper, Step, StepLabel, Divider, IconButton } from '@mui/material';
import { CreditCard, Smartphone, Truck, Home, ShoppingCart, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const steps = ['Address', 'Payment', 'Review'];

function CheckoutPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [address, setAddress] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    zip: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('card');

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleAddressChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Shipping Address</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" value={address.name} onChange={(e) => handleAddressChange('name', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" type="email" value={address.email} onChange={(e) => handleAddressChange('email', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Phone" value={address.phone} onChange={(e) => handleAddressChange('phone', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Street Address" value={address.street} onChange={(e) => handleAddressChange('street', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" value={address.city} onChange={(e) => handleAddressChange('city', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="ZIP Code" value={address.zip} onChange={(e) => handleAddressChange('zip', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Payment Method</Typography>
            <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <Card sx={{ mb: 2, p: 2 }}>
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CreditCard />
                      <Box>
                        <Typography variant="subtitle1">Credit/Debit Card</Typography>
                        <Typography variant="body2" color="text.secondary">Visa, MasterCard, American Express</Typography>
                      </Box>
                    </Box>
                  }
                />
                {paymentMethod === 'card' && (
                  <Box sx={{ mt: 2, pl: 4 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Card Number" placeholder="1234 5678 9012 3456" />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth label="Expiry Date" placeholder="MM/YY" />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth label="CVV" placeholder="123" />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Card>
              <Card sx={{ mb: 2, p: 2 }}>
                <FormControlLabel
                  value="upi"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Smartphone />
                      <Box>
                        <Typography variant="subtitle1">UPI</Typography>
                        <Typography variant="body2" color="text.secondary">Paytm, Google Pay, PhonePe</Typography>
                      </Box>
                    </Box>
                  }
                />
                {paymentMethod === 'upi' && (
                  <Box sx={{ mt: 2, pl: 4 }}>
                    <TextField fullWidth label="UPI ID" placeholder="yourname@upi" />
                  </Box>
                )}
              </Card>
              <Card sx={{ p: 2 }}>
                <FormControlLabel
                  value="cod"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Truck />
                      <Box>
                        <Typography variant="subtitle1">Cash on Delivery</Typography>
                        <Typography variant="body2" color="text.secondary">Pay when you receive the order</Typography>
                      </Box>
                    </Box>
                  }
                />
              </Card>
            </RadioGroup>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Order Review</Typography>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Shipping Address</Typography>
                <Typography>{address.name}</Typography>
                <Typography>{address.street}</Typography>
                <Typography>{address.city}, {address.zip}</Typography>
                <Typography>{address.email} | {address.phone}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Payment Method</Typography>
                <Typography>
                  {paymentMethod === 'card' && 'Credit/Debit Card'}
                  {paymentMethod === 'upi' && 'UPI'}
                  {paymentMethod === 'cod' && 'Cash on Delivery'}
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Order Summary</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Premium Wireless Headphones (x1)</Typography>
                  <Typography>$299.00</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography>Smart Watch (x2)</Typography>
                  <Typography>$500.00</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total</Typography>
                  <Typography variant="h6">$799.00</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mt: 7, mx: 2, maxWidth: '800px', mx: 'auto' }}>      {/* Simple Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, boxShadow: 1 }}>
        <Button startIcon={<Home />} onClick={() => navigate('/Ecommerce')}>Home</Button>
        <Typography variant="h5">Checkout</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => navigate('/CartPage')}><ShoppingCart /></IconButton>
          <IconButton onClick={() => navigate('/UserDashboard')}><User /></IconButton>
        </Box>
      </Box>      <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>Checkout</Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={activeStep === steps.length - 1 ? () => navigate('/UserDashboard') : handleNext}
        >
          {activeStep === steps.length - 1 ? 'Place Order' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}

export default CheckoutPage;